import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { NetworkStatus, useQuery } from "@apollo/client";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import dayjs from "dayjs";

import {
  GetTimestreamDataQuery,
  GetTimestreamDataQueryVariables,
} from "../../../../../../API";
import { useDateRange } from "../../../../../../common/components/datePicker/modelsDatePickers";
import { useCustomerIdGuard } from "../../../../../../common/hooks/useCustomerIdGuard";
import { GET_TIMESTREAM_DATA } from "../../../../../../common/operations/queries";
import { convertDateToUTC } from "../../../../../../common/utils/timestampUtils";
import { DEFAULT_EVENTS_LIMIT, EVENTS_DATE_FORMAT } from "../events.constants";
import { LoadMoreType } from "../events.models";
import { useEventsFilters } from "./useEventsFilters";

export const DEFAULT_EVENTS_SORT_MODEL: GridSortModel = [
  {
    field: "timestamp",
    sort: "desc",
  },
];

export const useGetTimestreamData = (pageSize?: number) => {
  const selectedCustomerId = useCustomerIdGuard();
  const { model } = useParams();
  const dates = useDateRange();
  let queryInputOffset = 0;

  const startDate = dates?.from
    ? dayjs(convertDateToUTC(dates.from)).format(EVENTS_DATE_FORMAT)
    : null;
  const endDate = dates?.to
    ? dayjs(convertDateToUTC(dates.to)).format(EVENTS_DATE_FORMAT)
    : null;

  const {
    categoryVariable,
    companyVariable,
    locationVariable,
    tagLocationsVariable,
    licensePlateVariable,
  } = useEventsFilters();

  const locationId = locationVariable?.value ?? "";
  const companyName = companyVariable?.value ?? "";
  const categoryName = categoryVariable?.title;
  const licensePlateValue = licensePlateVariable?.value ?? "";

  let locations: Array<string> | null = [];

  if (locationId) {
    locations = [locationId ?? ""];
  } else if (tagLocationsVariable) {
    locations = tagLocationsVariable
      .map(l => l?.value ?? "")
      .filter(f => f !== "");

    if (locations.length === 0) {
      locations = null;
    }
  }

  const [sortModel, setSortModel] = useState<GridSortModel>(
    DEFAULT_EVENTS_SORT_MODEL
  );

  const { data, loading, fetchMore, networkStatus } = useQuery<
    GetTimestreamDataQuery,
    GetTimestreamDataQueryVariables
  >(GET_TIMESTREAM_DATA, {
    skip: !selectedCustomerId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    variables: {
      input: {
        customerId: selectedCustomerId,
        model: model ?? "",
        dateRange: JSON.stringify({ startDate, endDate }),
        locations,
        categoryName,
        companyName,
        licensePlate: licensePlateValue,
        offset: 0,
        limit: pageSize ?? DEFAULT_EVENTS_LIMIT,
        nextToken: null,
      },
    },
  });

  const loadMore: LoadMoreType = async (
    offset: number,
    limit: number,
    nextToken?: string | null
  ) => {
    queryInputOffset = offset;

    return await fetchMore({
      variables: {
        input: {
          customerId: selectedCustomerId,
          model: model ?? "",
          dateRange: JSON.stringify({ startDate, endDate }),
          locations,
          categoryName,
          companyName,
          licensePlate: licensePlateValue,
          offset,
          limit,
          nextToken,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return { getTimestreamData: fetchMoreResult.getTimestreamData };
      },
    });
  };

  const parsedQueryResult = useMemo(() => {
    try {
      return data?.getTimestreamData
        ? JSON.parse(data.getTimestreamData as string)
        : [];
    } catch (error) {
      console.error("Error parsing query result", error);

      return [];
    }
  }, [data]);

  const nextToken = parsedQueryResult?.nextToken;

  /** Pagination model and Filters */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: pageSize ?? DEFAULT_EVENTS_LIMIT,
  });

  const [filters, setFilters] = useState({
    startDate,
    endDate,
    locations,
    companyName,
    categoryName,
    licensePlateValue,
  });

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const page = model.pageSize !== paginationModel.pageSize ? 0 : model.page;

    if (loadMore) {
      const offset = page * model.pageSize;

      queryInputOffset = offset;

      loadMore(offset, model.pageSize, nextToken).then(() => {
        setPaginationModel(prev => ({
          ...prev,
          page,
          pageSize: model.pageSize,
        }));
      });
    }
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  useEffect(() => {
    const locationsFilterChanged =
      filters.locations?.toString() !== locations?.toString();

    if (
      startDate !== filters.startDate ||
      endDate !== filters.endDate ||
      locationsFilterChanged ||
      companyName !== filters.companyName ||
      categoryName !== filters.categoryName ||
      licensePlateValue !== filters.licensePlateValue
    ) {
      setPaginationModel({
        page: 0,
        pageSize: pageSize ?? DEFAULT_EVENTS_LIMIT,
      });

      setSortModel(DEFAULT_EVENTS_SORT_MODEL);

      setFilters(prevState => {
        return {
          ...prevState,
          startDate,
          endDate,
          locations,
          companyName,
          categoryName,
          licensePlateValue,
        };
      });
    }
  }, [
    startDate,
    endDate,
    locations,
    companyName,
    categoryName,
    licensePlateValue,
    filters.locations,
    filters.startDate,
    filters.endDate,
    filters.companyName,
    filters.categoryName,
    filters.licensePlateValue,
    pageSize,
  ]);

  useEffect(() => {
    setSortModel(DEFAULT_EVENTS_SORT_MODEL);

    setPaginationModel({
      page: 0,
      pageSize: pageSize ?? DEFAULT_EVENTS_LIMIT,
    });
  }, [selectedCustomerId]);

  const hasNextPage = useMemo(() => {
    return nextToken !== undefined;
  }, [nextToken]);

  const isActivePageChange = useMemo(() => {
    return hasNextPage && !loading;
  }, [hasNextPage, loading]);

  const queryInput = useMemo(() => {
    return {
      input: {
        customerId: selectedCustomerId,
        model: model ?? "",
        dateRange: JSON.stringify({ startDate, endDate }),
        locations,
        companyName,
        licensePlate: licensePlateValue,
        offset: queryInputOffset,
        limit: pageSize ?? DEFAULT_EVENTS_LIMIT,
        nextToken,
      },
    };
  }, [
    selectedCustomerId,
    startDate,
    endDate,
    locations,
    companyName,
    licensePlateValue,
    queryInputOffset,
    pageSize,
    nextToken,
  ]);

  const parsedResult = useMemo(() => {
    return parsedQueryResult?.body ?? [];
  }, [parsedQueryResult]);

  return {
    parsedResult,
    nextToken,
    loading: loading || networkStatus === NetworkStatus.refetch,
    loadMore,

    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    hasNextPage,
    isActivePageChange,
    queryInput,
  } as const;
};
